'use client'
import {
  acceptTermsValidator,
  phoneValidator,
  requiredFieldValidator,
} from '@/utils/validator'
import { yupResolver } from '@hookform/resolvers/yup'
import cn from 'classnames'
import { forwardRef, useCallback } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { object as yupObject, string as yupString } from 'yup'

import { AcceptCheckbox } from '@/components/AcceptCheckbox'
import { Button } from '@/components/Button'
import { FormInput } from '@/components/FormInput'
import { Loader } from '@/components/Loader'
import { TextArea } from '@/components/TextArea'

import { getHelp } from '@/app/actions/help/actions'
import { marketingStorage } from '@/store/MarketingStorage'
import { YANDEX_METRIKA, YANDEX_METRIKA_COUNTER } from '@/config'
import { useCaptcha } from '@/hooks/Captcha'
import { HelpTypeSite } from '@/services/Api/generated'
import { maxPopupStore, maxRequestFormTexts } from '@/store/MaxPopup.store'
import styles from './HelpForm.module.scss'

interface IFormData {
  name: string
  phone: string
  comment?: string
  accept: boolean
}

const schema = yupObject().shape({
  name: requiredFieldValidator,
  phone: phoneValidator,
  comment: yupString(),
  accept: acceptTermsValidator,
})

type HelpFormProps = {
  className?: string
}
export const HelpForm = forwardRef<HTMLFormElement, HelpFormProps>(
  ({ className }, ref) => {
    const { getToken } = useCaptcha()

    const {
      register,
      setValue,
      handleSubmit,
      formState: { isSubmitting, errors },
    } = useForm<IFormData>({
      mode: 'onChange',
      resolver: yupResolver(schema),
    })

    const onSubmit: SubmitHandler<IFormData> = useCallback(
      async (data) => {
        const captchaToken = await getToken()
        const response = await getHelp(
          {
            type: HelpTypeSite.FreeConsultation,
            data: {
              sourceUrl: window.location.href.split('?')[0],
              phone: data.phone,
              name: data.name,
              comment: data.comment,
              utmClientId: marketingStorage.getClientId() ?? undefined,
            },
          },
          captchaToken
        )
        if (response.status) {
          marketingStorage.clearClientId()
          if (YANDEX_METRIKA) {
            // @ts-ignore
            ym(YANDEX_METRIKA_COUNTER, 'reachGoal', 'FORM_CONSULTATION_SUBMIT')
          }
          setValue('phone', '')
          setValue('name', '')
          setValue('comment', '')
          setValue('accept', false)
          maxPopupStore.show({
            sourceSite: response.data.params,
            ...maxRequestFormTexts,
          })
        } else {
          toast.error('Что то пошло не так! Попробуйте позже')
        }
      },
      [setValue, getToken]
    )

    return (
      <form
        className={cn(styles.container, className)}
        onSubmit={handleSubmit(onSubmit)}
        ref={ref}
      >
        <div className={styles.title}>
          Оставьте заявку на бесплатную консультацию
        </div>
        <div className={styles.content}>
          <div className={styles.description}>
            Оставьте номер телефона, мы перезвоним и проконсультируем
            по&nbsp;всем интересующим вас вопросам
          </div>
          <div className={styles.form}>
            <div className={styles.row}>
              <FormInput
                placeholder="Имя"
                className={styles.input}
                error={errors.name?.message}
                {...register('name')}
              />
              <FormInput
                mask="+{7} (000) 000-00-00"
                placeholder="+7 (999) 999-99-99"
                className={styles.input}
                error={errors.phone?.message}
                {...register('phone')}
              />
            </div>
            <TextArea
              placeholder="Комментарий"
              className={styles.input}
              error={errors.comment?.message}
              {...register('comment')}
            />
            <Button
              type="submit"
              className={styles.button}
              disabled={isSubmitting}
              compactHover
            >
              Отправить
              {isSubmitting ? <Loader onTop /> : null}
            </Button>
          </div>
          <AcceptCheckbox<IFormData>
            className={styles.agree}
            register={register}
            error={errors.accept?.message}
          />
        </div>
        <div className={styles.overflow}>
          <img src="/images/dots.svg" className={styles.dots} alt="" />
        </div>
      </form>
    )
  }
)
